// @ts-strict-ignore
import {useRef} from 'react';

import {Persistence} from 'common/report/persistence';
import type {ContextPanelReactProps} from 'common/widgets/context-panel';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {Translatable} from 'common/i18n';

import {NUM_RECENT_ITEMS} from './constants';

export interface TooltipContextActionResult {
  handled: boolean;
  openedModal: boolean;
}

/**
 * @deprecated We are in the process of decoupling child-menu components. Please don't
 * add more values to this interface. Add individually to each child menu's Attrs instead.
 */
export interface ChildMenuBaseAttrs {
  'event-names'?: string[];
  'project-id'?: number;
  readonly?: boolean;
  'workspace-id'?: number;
  'track-name'?: string;
}

export interface ContextPanelRendererParams {
  availableContexts?: Partial<IronContextRegistry>;
  tooltipData: ContextPanelData;
  contextPanelProps?: ContextPanelReactProps;
  /** handles deleted entities from the context panel */
  handleSelect: (...args) => void;
  /** handles a deleted interaction from the context panel */
  handleDelete: (...args) => void;
  /** handles when the inner contents of the context panel are hovered in */
  handleHoverIn?: () => void;
  /** handles when the inner contents of the context panel are hovered out */
  handleHoverOut?: () => void;
  /** defines which tracking context the context panel should track to */
  track: (...args) => void;
  /** passes menu specific attributes for the context panel to render with */
  additionalArgs?: Obj;
  key?: string;
}

export type ContextPanelRenderer = (params: ContextPanelRendererParams) => React.ReactNode;

export class ChildMenuItemsCache {
  private declare _persistence: Persistence;
  private declare size: number;

  private constructor(persistence: Persistence | null, lruSize = NUM_RECENT_ITEMS) {
    this._persistence = persistence;
    this.size = lruSize;
  }

  static createCache(
    userId: number,
    projectId: number,
    persistenceKey: string,
    workspaceId: number,
    lruSize = NUM_RECENT_ITEMS,
  ) {
    let persistence: Persistence;
    if (userId && (projectId || workspaceId)) {
      persistence = Persistence.createCachedNamespace(
        // prefix the workspace so there are no collisions with project ids
        workspaceId ? `wksp-${workspaceId}` : projectId,
        userId,
        persistenceKey,
      );
    }
    return new ChildMenuItemsCache(persistence, lruSize);
  }

  get persistence() {
    return this._persistence;
  }

  public getRecentItems(subKey, limit = null) {
    if (this.persistence) {
      const items = this.persistence.getObject(`recent-items-${subKey}`) || [];
      return limit ? items.slice(0, limit) : items;
    }
    return [];
  }

  public addRecentItem(subKey, item, getItemKey) {
    if (this.persistence) {
      let items = this.getRecentItems(subKey);
      items = items.filter((existingItem) => getItemKey(existingItem) !== getItemKey(item));
      items.unshift(item);
      items = items.slice(0, this.size);
      this.persistence.setObject(`recent-items-${subKey}`, items);
    }
  }
}

export interface ChildMenuInterface {
  contextPanelRenderer?: ContextPanelRenderer;
  handleTooltipContextAction?: (Event) => TooltipContextActionResult;
  getSelectedHoverTooltipData?: () => ContextPanelData;

  selectedLabel?: () => Translatable;
  getPrefixLabel?: () => Translatable;
  selectedBrowserTooltipLabel?: () => Translatable;
  selectedLeftIcon?: () => string;
  selectedRightIcon?: () => string;
  handleLabelMouseEnter?: () => void;
  handleLabelMouseLeave?: () => void;

  getSelections?: () => string[];
  getSelectedTrackingProps?: () => Obj;

  canOpenMenu: () => boolean;
  handleCloseMenu?: () => void;
  shouldCloseOnSelect: () => boolean;

  isUnsavedCustomEntity?: () => boolean;

  // Panel component methods called by query-block
  initialized?: boolean;
  setParams?: (any) => void;

  // Used by tests
  update?: (any) => void;
  el?: any;
}

export const useChildMenuCache = ({
  childMenuName,
  userId,
  projectId,
  workspaceId,
}: {
  childMenuName: string;
  userId: number;
  projectId: number;
  workspaceId: number;
}): ChildMenuItemsCache => {
  const cacheRef = useRef<ChildMenuItemsCache>(null);
  if (cacheRef.current === null) {
    cacheRef.current = ChildMenuItemsCache.createCache(userId, projectId, childMenuName, workspaceId);
  }
  return cacheRef.current;
};
